import React, { useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Button, Label, Input } from "reactstrap";
import { every, find, filter } from "lodash";
import { compose, withHandlers, withPropsOnChange } from "recompose";
import { isEmail } from "validator";
import { Control, actions } from "react-redux-form";
import { Trans, withTranslation, useTranslation } from "react-i18next";
import {
  required,
  SubmitButton,
  withLocalForm,
  FormGroup,
  withModal,
  onMount,
} from "components/common";
import PropTypes from "prop-types";
import { XeroClient } from "services";
import { NotificationManager } from "react-notifications";
import { populateTemplate } from "services/templates";
import Select from "react-select";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import { useOrganisationContext } from "hooks";

export const DocumentEmailer = ({
  isModalOpen,
  toggleModal,
  LocalForm,
  setIsOpen,
  onSubmit,
  dispatch,
  templates,
  state,
  onSentToContact,
  language,
  ...props
}
) => {
  const { i18n } = useTranslation()
  const { selectedOrganisation } = useOrganisationContext()

  const availableLangs = selectedOrganisation?.subscribed_features?.hesaablanguage;

  const filteredTemplates = templates?.filter(template => template.language === 'en' || (availableLangs && availableLangs.includes(template.language)));

  return (
    <div>
      <Modal isOpen={isModalOpen} toggle={toggleModal} className={i18n.language === "ar" && "rtl"}>
        <ModalHeader><Trans>Send</Trans></ModalHeader>
        <ModalBody>
          <LocalForm
            validators={{
              email: {
                required,
                isEmail: (v) =>
                  `${(!v ? true : every(v.split(";"), (y) => isEmail(y)))}`,
              },
              subject: { required },
              content: { required },
            }}
          >
            <FormGroup label="To" model=".email">
              <Control.text model=".email" />
              <span className="form-text text-muted small">
                <Trans>
                  Separate multiple email addresses with a semicolon (;)
                </Trans>
              </span>
            </FormGroup>
            <div className="row my-2">
              <div className="col">
                <FormGroup>
                  <Label><Trans>From</Trans></Label>
                  <Input type="text" name="from_name" id="" disabled value={state.from_name} />
                </FormGroup>
              </div>
              <div className="col">
                <FormGroup>
                  <Label><Trans>Reply To</Trans></Label>
                  <Input type="text" name="from_name" id="" disabled value={state.reply_to} />
                </FormGroup>
              </div>
            </div>
            <FormGroup label="Template" model=".selectedTemplate" className="my-2">
              <Control
                component={Select}
                model=".selectedTemplate"
                labelKey="description"
                valueKey="id"
                options={filteredTemplates}
                clearable={false}
              />
            </FormGroup>
            <FormGroup label="Subject" model=".subject" className="my-2">
              <Control.text model=".subject" />
            </FormGroup>
            <FormGroup label="Message" model=".content" className="my-2">
              <Control.textarea rows="10" model=".content" />
            </FormGroup>
            {!!onSentToContact && (
              <FormGroup label="Mark as sent" check model=".mark_as_sent" className="my-2">
                <Control.checkbox model=".mark_as_sent" />
              </FormGroup>
            )}
            <div className="d-flex justify-content-end">
              <Button color="secondary" className="mr-2" onClick={toggleModal}>
                <Trans>Cancel</Trans>
              </Button>
              <SubmitButton
                onClick={() => {
                  dispatch(actions.submit("local", onSubmit(state)));
                }}
              >
                Send
              </SubmitButton>
            </div>
          </LocalForm>
        </ModalBody>
      </Modal>
    </div>
  );
}

export const enhance = compose(
  withOrganisationContextConsumer,
  withTranslation(),
  onMount(
    ({ type, language, fetchOrganisation, fetchUser }) =>
      () =>
        XeroClient()
          .get(`email_templates?language=${language}&type=${type}`)
          .get("data")
          .then((templates) => {
            return Promise.props({
              templates: filter(templates, (i) => i.type === type),
              selectedTemplate:
                find(templates, (i) => i.type === type && i.is_default && i.language === language) || null,
              organisation: fetchOrganisation(),
              user: fetchUser(),
            });
          })
  ),
  withLocalForm({
    initialState: ({ initialState, selectedTemplate, user }) => ({
      ...initialState,
      selectedTemplate,
      from_name: user.full_name,
      reply_to: user.email,
    }),
  }),
  withPropsOnChange(
    (props, nextProps) => {
      return props.state.selectedTemplate !== nextProps.state.selectedTemplate;
    },
    ({
      state: { selectedTemplate },
      dispatch,
      type,
      data: tmpData,
      organisation: Organisation,
    }) => {
      if (!selectedTemplate) return;

      const normalisedTmpData = Array.isArray(tmpData) ? tmpData : [tmpData]; // check if tempData is an array and then pass the data


      const contactIDs = normalisedTmpData?.map(item => item.Contact.ContactID);
      const uniqueContactIDs = [...new Set(contactIDs)];
      const contactID = uniqueContactIDs.length === 1 ? uniqueContactIDs[0] : null;

        if (contactID !== null) {
          XeroClient()
            .get(`contacts/${contactID}`)
            .then(response => {
              if (!response.data) {
                throw new Error('Failed to fetch contact data');
              }
              return response.data; 
            })
            .catch(error => {
              console.error('Error fetching contact:', error);
              return null;
            })
            .then(contactData => {
              if (contactData) {
                dispatch(
                  actions.change("local.email", contactData.EmailAddress)
                );
              }
            });
        }


      const data = {
        ...tmpData,
        Organisation,
      };
      const subject = populateTemplate(selectedTemplate.subject, type, data);
      const content = populateTemplate(selectedTemplate.content, type, data);
      dispatch(actions.change("local.subject", subject));
      dispatch(actions.change("local.content", content));
    }
  ),
  withModal(),
  withHandlers({
    onSubmit:
      ({
        xero,
        filename,
        dispatch,
        t,
        template,
        mark_as_sent,
        setIsModalOpen,
        type,
        onSentToContact,
        ...props
      }) =>
        (item) => {
          dispatch(actions.setPending("local"));
          const xero = XeroClient();
          const { mark_as_sent } = item;

          xero
            .post("all/email_document", {
              ...item,
              email: item.email.split(";"),
              filename,
              type,
              template,
              IsSimplified:props.data?.IsSimplified,
              invoiceID: props.data?.InvoiceID || props.data?.CreditNoteID,
            })
            .tap(() => {
              NotificationManager.success(t("Email successfully sent"));
              setIsModalOpen(false);
              return onSentToContact && mark_as_sent ? onSentToContact() : null;
            })
            .finally(() => dispatch(actions.setPending("local", false)));
        },
  })
);

const EnhancedDocumentEmailer = enhance(DocumentEmailer);

EnhancedDocumentEmailer.propTypes = {
  template: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
};

export default EnhancedDocumentEmailer;
